import React, { useState } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Home, 
  BookOpen, 
  Target, 
  Award, 
  Calendar, 
  Settings,
  CheckCircle2,
  Play,
  Clock,
  TrendingUp,
  Star
} from 'lucide-react';
import { CurrentLearningTab } from './tabs/CurrentLearningTab';
import { ExploreTab } from './tabs/ExploreTab';
import { CompletedTab } from './tabs/CompletedTab';
import { RecommendedTab } from './tabs/RecommendedTab';

type TabType = 'dashboard' | 'current' | 'explore' | 'completed' | 'recommended';

export function StudentDashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, description: 'Overview & Quick Actions' },
    { id: 'current', label: 'Current Learning', icon: BookOpen, description: 'Active Courses & Progress' },
    { id: 'explore', label: 'Explore', icon: Target, description: 'Discover New Content' },
    { id: 'completed', label: 'Completed', icon: CheckCircle2, description: 'Finished Courses' },
    { id: 'recommended', label: 'Recommended', icon: Star, description: 'Personalized Suggestions' }
  ];

  const quickStats = [
    { label: 'Active Courses', value: '3', icon: BookOpen, color: 'bg-blue-50' },
    { label: 'Completed', value: '12', icon: CheckCircle2, color: 'bg-green-50' },
    { label: 'Study Streak', value: '7 days', icon: TrendingUp, color: 'bg-purple-50' },
    { label: 'Total Hours', value: '45h', icon: Clock, color: 'bg-orange-50' }
  ];

  const recentActivity = [
    { title: 'Philosophy of Mind', progress: 85, lastAccessed: '2 hours ago', type: 'Continue' },
    { title: 'Logic and Reasoning', progress: 60, lastAccessed: '1 day ago', type: 'Continue' },
    { title: 'Ethics in Practice', progress: 100, lastAccessed: '3 days ago', type: 'Review' }
  ];

  const renderMainContent = () => {
    switch (activeTab) {
      case 'current':
        return <CurrentLearningTab />;
      case 'explore':
        return <ExploreTab />;
      case 'completed':
        return <CompletedTab />;
      case 'recommended':
        return <RecommendedTab />;
      default:
        return (
          <div className="space-y-6">
            {/* Welcome Section */}
            <Card className="bg-white border border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-black mb-2">Welcome back, Student!</h1>
                    <p className="text-gray-600">Ready to continue your learning journey?</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Today's Goal</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={65} className="w-24" />
                      <span className="text-sm font-medium">65%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {quickStats.map((stat, index) => (
                <Card key={index} className="bg-white border border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${stat.color}`}>
                        <stat.icon className="h-5 w-5 text-gray-700" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-black">{stat.value}</p>
                        <p className="text-sm text-gray-600">{stat.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Recent Activity */}
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-black">Continue Learning</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentActivity.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-semibold text-black">{item.title}</h3>
                      <div className="flex items-center gap-4 mt-2">
                        <Progress value={item.progress} className="flex-1 max-w-48" />
                        <span className="text-sm text-gray-600">{item.progress}%</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">Last accessed {item.lastAccessed}</p>
                    </div>
                    <Button size="sm" className="ml-4">
                      <Play className="h-4 w-4 mr-2" />
                      {item.type}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black">Today's Schedule</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Clock className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="font-medium text-black">Philosophy Session</p>
                        <p className="text-sm text-gray-600">2:00 PM - 3:30 PM</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="font-medium text-black">Ethics Assignment Due</p>
                        <p className="text-sm text-gray-600">Tomorrow, 11:59 PM</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black">Achievements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Award className="h-8 w-8 text-yellow-500" />
                      <div>
                        <p className="font-medium text-black">Week Warrior</p>
                        <p className="text-sm text-gray-600">7-day study streak</p>
                      </div>
                      <Badge variant="secondary" className="ml-auto">New!</Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <Star className="h-8 w-8 text-blue-500" />
                      <div>
                        <p className="font-medium text-black">Logic Master</p>
                        <p className="text-sm text-gray-600">Completed Logic course</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );
    }
  };

  const sidebarBottom = (
    <button className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-600 hover:bg-gray-200 hover:text-black transition-all duration-200">
      <Settings className="h-5 w-5 flex-shrink-0" />
      <span className="overflow-hidden transition-all duration-300 whitespace-nowrap">
        Settings
      </span>
    </button>
  );

  return (
    <DashboardLayout
      title="Learning Hub"
      menuItems={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId: string) => setActiveTab(tabId as TabType)}
      sidebarBottom={sidebarBottom}
    >
      {renderMainContent()}
    </DashboardLayout>
  );
} 